import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import {
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	CircularProgress,
	Alert,
} from "@mui/material";
import RtPrice from "../RtPrice"; // Assuming RtPrice is a named export
import useAxiosPrivate from "../../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../../hooks/useAuth";
import useRealTimePrice from "../../../../../hooks/useRealTimePrice";

// Define your actual API endpoint and method
const BUY_ORDER_API_URL = "/market-buy-order"; // Example endpoint
const DEMO_BUY_ORDER_API_URL = "/demo-market-buy-order";

const TradeForm = React.memo((props) => {
	const { coinPrice, coinSymbol } = props;

	// State management
	const [usdAmount, setUsdAmount] = useState("");
	const [takeProfitPrice, setTakeProfitPrice] = useState("");
	const [stopLossPrice, setStopLossPrice] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMessage, setDialogMessage] = useState("");
	const [dialogType, setDialogType] = useState("success");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	// console.log(rtPrice);
	// console.log(coinSymbol);
	// const realTimePrice = useRealTimePrice(coinSymbol);

	// console.log(coinSymbol);
	// if (coinSymbol) {
	// 	console.log(rtPrice);
	// }

	const axiosPrivate = useAxiosPrivate();
	const { auth, accountMode } = useAuth();
	// const accountBalance =
	// 	typeof auth?.balance === "undefined" ? "loading..." : `$${auth?.balance}`;

	// const demoAccountBalance =
	// 	typeof auth?.balance === "undefined" ? "loading..." : `$${auth?.balance}`;

	const activeEndPoint =
		accountMode === "real" ? BUY_ORDER_API_URL : DEMO_BUY_ORDER_API_URL;

	// Calculate approximate crypto amount based on USD amount and current price
	const approximateCryptoAmount = useMemo(() => {
		const parsedUsdAmount = parseFloat(usdAmount);
		if (
			isNaN(parsedUsdAmount) ||
			parsedUsdAmount <= 0 ||
			!coinPrice ||
			coinPrice <= 0
		) {
			return 0;
		}
		return (parsedUsdAmount / coinPrice).toFixed(8);
	}, [usdAmount, coinPrice]);

	// Handlers
	const handleUsdAmountChange = (e) => {
		const value = e.target.value;
		if (/^\d*\.?\d*$/.test(value) || value === "") {
			setUsdAmount(value);
		}
	};
	const handleTakeProfitPriceChange = (e) => {
		const value = e.target.value;
		if (/^\d*\.?\d*$/.test(value) || value === "") {
			setTakeProfitPrice(value);
		}
	};
	const handleStopLossPriceChange = (e) => {
		const value = e.target.value;
		if (/^\d*\.?\d*$/.test(value) || value === "") {
			setStopLossPrice(value);
		}
	};

	const handleCloseDialog = () => setDialogOpen(false);

	const handleSubmit = async () => {
		setError("");

		const parsedUsdAmount = parseFloat(usdAmount);
		const parsedTPPrice = parseFloat(takeProfitPrice);
		const parsedSLPrice = parseFloat(stopLossPrice);
		const currentPrice = parseFloat(coinPrice);

		if (isNaN(parsedUsdAmount) || parsedUsdAmount <= 0) {
			setError("Please enter a valid positive amount in USD.");
			return;
		}

		if (
			takeProfitPrice !== "" &&
			(isNaN(parsedTPPrice) || parsedTPPrice <= 0)
		) {
			setError("Please enter a valid positive Take Profit price.");
			return;
		}

		if (
			takeProfitPrice !== "" &&
			currentPrice &&
			parsedTPPrice <= currentPrice
		) {
			setError(
				"Take Profit price must be higher than the current market price."
			);
			return;
		}

		if (stopLossPrice !== "" && (isNaN(parsedSLPrice) || parsedSLPrice <= 0)) {
			setError("Please enter a valid positive Stop Loss price.");
			return;
		}

		if (stopLossPrice !== "" && currentPrice && parsedSLPrice >= currentPrice) {
			setError("Stop Loss price must be lower than the current market price.");
			return;
		}

		const orderData = {
			id: auth?.id,
			coinSymbol: coinSymbol,
			orderType: "market",
			usdAmount: parsedUsdAmount,
			takeProfitPrice: parsedTPPrice > 0 ? parsedTPPrice : null,
			stopLossPrice: parsedSLPrice > 0 ? parsedSLPrice : null,
			marketPrice: currentPrice,
		};

		setLoading(true);
		setError("");
		try {
			const response = await axiosPrivate.post(activeEndPoint, orderData);
			if (response.status === 200 || response.status === 201) {
				const executedTrade = response.data;
				setDialogMessage(
					`Market Buy Order for  ${coinSymbol} executed successfully! Your balance has been updated.`
				);
				// ${executedTrade.executedCryptoAmount.toFixed(8)}
				setDialogType("success");
				setUsdAmount("");
				setTakeProfitPrice("");
				setStopLossPrice("");
			} else {
				setDialogMessage(response.data?.message || "Failed to submit trade");
				setDialogType("error");
			}
		} catch (error) {
			console.error("Trade submission error:", error);
			if (error.response) {
				setDialogMessage(
					error.response.data?.message || `Error: ${error.response.status}`
				);
				setDialogType("error");
			} else if (error.request) {
				setDialogMessage("Network Error: No response from server.");
				setDialogType("error");
			} else {
				setDialogMessage("Error: " + error.message);
				setDialogType("error");
			}
		} finally {
			setLoading(false);
			setDialogOpen(true);
		}
	};

	return (
		<Stack spacing={3} sx={{ width: "100%" }}>
			<RtPrice sym={coinSymbol} />

			{/* <Typography sx={{ fontSize: "1.2rem", color: "gray" }}>
				wallet balance: <span className="text-black">{accountBalance}</span>
			</Typography> */}
			<TextField
				label="Asset Safety Price"
				type="number"
				value={coinPrice ? parseFloat(coinPrice).toFixed(8) : ""}
				InputProps={{ readOnly: true }}
				sx={{ width: "100%", bgcolor: "lightgray", color: "white" }}
				helperText={`  ${coinSymbol}`}
				variant="filled"
			/>

			<Typography sx={{ fontSize: "0.9rem", color: "gray" }}>
				Enter the total USD amount you wish to spend on this buy order.
			</Typography>
			<TextField
				label="Total USD Amount to Spend"
				type="text"
				value={usdAmount}
				onChange={handleUsdAmountChange}
				sx={{ width: "100%" }}
				inputProps={{ step: 0.01, min: 0 }}
				helperText={`You will buy approximately ${approximateCryptoAmount} ${coinSymbol}`}
			/>

			<Typography sx={{ fontSize: "0.9rem", color: "gray" }}>
				Enter the target price for taking profit.
			</Typography>
			<TextField
				label="Take Profit Price"
				type="text"
				value={takeProfitPrice}
				onChange={handleTakeProfitPriceChange}
				sx={{ width: "100%" }}
				inputProps={{ step: 0.0001, min: 0 }}
			/>

			<Typography sx={{ fontSize: "0.9rem", color: "gray" }}>
				Enter the target price for stopping loss.
			</Typography>
			<TextField
				label="Stop Loss Price"
				type="text"
				value={stopLossPrice}
				onChange={handleStopLossPriceChange}
				sx={{ width: "100%" }}
				inputProps={{ step: 0.0001, min: 0 }}
			/>

			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}

			<Button
				variant="contained"
				onClick={handleSubmit}
				disabled={loading || !coinPrice}
				sx={{
					width: "8rem",
					p: "0.5rem",
					bgcolor: "green",
					color: "white",
					mx: "auto",
					"&:hover": { bgcolor: "darkgreen" },
				}}>
				{loading ? <CircularProgress size={24} color="inherit" /> : "Buy"}
			</Button>

			<Dialog open={dialogOpen} onClose={handleCloseDialog}>
				<DialogTitle>
					{dialogType === "success" ? "Success" : "Error"}
				</DialogTitle>
				<DialogContent>
					<Typography>{dialogMessage}</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Close</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
});

export default TradeForm;
